import Button from '@doordash/component-button'
import { NumberField } from '@doordash/component-fields'
import Separator from '@doordash/component-separator'
import {
  InlineChildren,
  Inset,
  Media,
  Spacing,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { Interpolate } from '@doordash/lib-i18n'
import { noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { i18nKeys } from 'translationTypes'
import { AusBackgroundCheckStep } from '.'
import * as events from '../../../../events'
import i18n from '../../../../i18n'
import * as DA from '../../../../typeModules/events'
import Form from '../../../../utilities/Form'
import { formatErrorKeys } from '../../../../utilities/Form/utils'
import { isMobile } from '../../../../utilities/userAgent'
import { validators as sharedValidators } from '../../../../utilities/validators'
import { CommonTextUrl } from '../../../atoms/common-text-url'
import AbnGuide, { AbnGuideItem } from './abn-guide'
import AbnHowTo from './abn-guide/abn-how-to'
import AbnLookup from './abn-guide/abn-lookup'

export const APPLY_ABN_LINK =
  'https://www.abr.gov.au/business-super-funds-charities/applying-abn'

const FormSeparator = styled(Separator)`
  margin-top: ${Spacing.Large}px;
  margin-bottom: ${Spacing.Large}px;

  ${Media.Mobile`
    width: 100vw;
    margin-left: -${Spacing.XSmall}px;
    margin-top: ${Spacing.Small}px;
    margin-bottom: ${Spacing.None}px;
  `}
`
const defaultFields = {
  australianBusinessNumber: '',
}

interface AbnPageProps {
  required: boolean
  uniqueLink: string
  onSubmit: (
    uniqueLink: string,
    data: Record<string, string>,
    backgroundCheckStep: AusBackgroundCheckStep,
    isAbnRevisited?: boolean
  ) => Promise<void>
  trackBackgroundCheckSteps: (payload: unknown) => Promise<void>
}

function AbnPage(props: AbnPageProps) {
  const [addAbnLater, setAddAbnLater] = React.useState<boolean>(false)
  const [isAbnHowToOpen, setIsAbnHowToOpen] = React.useState<boolean>(false)
  const [isAbnLookupOpen, setIsAbnLookupOpen] = React.useState<boolean>(false)
  const [isAbnSkipLoading, setIsAbnSkipLoading] = React.useState<boolean>(false)

  const { trackBackgroundCheckSteps, uniqueLink, onSubmit, required } = props

  React.useEffect(() => {
    trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.ABN,
      action: DA.EventActions.Rendered,
    })
  }, [trackBackgroundCheckSteps])

  React.useEffect(() => {
    if (addAbnLater) {
      setIsAbnSkipLoading(true)
      trackBackgroundCheckSteps({
        page: DA.BackgroundCheckPages.ABN,
        action: DA.EventActions.Skipped,
      })

      const submitHandler = async () => {
        try {
          await handleSubmit(defaultFields)
          setIsAbnSkipLoading(false)
        } catch (e) {
          trackBackgroundCheckSteps({
            page: DA.BackgroundCheckPages.ABN,
            action: DA.EventActions.Error,
            value: formatErrorKeys(e, defaultFields, true),
          })
          setAddAbnLater(false)
          setIsAbnSkipLoading(false)
          throw e
        }
      }
      submitHandler()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAbnLater])

  const validators = {
    australianBusinessNumber: addAbnLater
      ? noop
      : sharedValidators.australianBusinessNumber,
  }

  const handleSubmit = async data =>
    await onSubmit(uniqueLink, data, AusBackgroundCheckStep.ABN)

  const handleGuideItemClick = (guideItem: AbnGuideItem) => {
    if (guideItem === AbnGuideItem.HOW_TO) {
      setIsAbnHowToOpen(true)
    }
    if (guideItem === AbnGuideItem.LOOKUP) {
      setIsAbnLookupOpen(true)
    }
  }

  return (
    <>
      {isAbnHowToOpen && <AbnHowTo onClose={() => setIsAbnHowToOpen(false)} />}
      {isAbnLookupOpen && (
        <AbnLookup onClose={() => setIsAbnLookupOpen(false)} />
      )}
      <StackChildren>
        <Text tag="h1" styles={Text.Styles.Title1}>
          {i18n.t(i18nKeys.backgroundCheck__abnLabel)}
        </Text>
        <Text styles={Text.Styles.Body1} color={Text.Colors.TextSecondary}>
          <Interpolate
            i18n={i18n}
            i18nKey={i18n.t(i18nKeys.backgroundCheck__abnSubtitle)}
            components={[
              <CommonTextUrl
                url={APPLY_ABN_LINK}
                textProps={{ styles: Text.Styles.Body1 }}
                openInBrowser
              />,
            ]}
          />
        </Text>
        <Form
          onSubmit={handleSubmit}
          validators={validators}
          fields={defaultFields}
        >
          {(fields, handleChange, handleSubmit, { isLoading }) => {
            return (
              <form onSubmit={handleSubmit}>
                <NumberField
                  id="australianBusinessNumber"
                  label={'ABN'}
                  max={99999999999}
                  placeholder="12 345 678 901"
                  onChange={handleChange.bind(this, 'australianBusinessNumber')}
                  autoComplete="off"
                  {...fields.australianBusinessNumber}
                />
                <FormSeparator />
                <StackChildren
                  size={
                    isMobile
                      ? StackChildren.Sizes.Small
                      : StackChildren.Sizes.Large
                  }
                >
                  <AbnGuide onItemClick={handleGuideItemClick} />
                  <Inset size={isMobile ? Inset.Sizes.None : Inset.Sizes.Small}>
                    <StackChildren size={StackChildren.Sizes.XxSmall} justify>
                      <Button
                        accessibilityDescribedBy={'submitAbn'}
                        onClick={async () => {
                          trackBackgroundCheckSteps({
                            page: DA.BackgroundCheckPages.ABN,
                            action: DA.EventActions.Continue,
                            value: required ? 'revisit' : 'first',
                          })
                          try {
                            await handleSubmit()
                          } catch (e) {
                            trackBackgroundCheckSteps({
                              page: DA.BackgroundCheckPages.ABN,
                              action: DA.EventActions.Error,
                              value: formatErrorKeys(e, fields, true),
                            })
                            throw e
                          }
                        }}
                        state={
                          isLoading && !addAbnLater
                            ? Button.States.Loading
                            : Button.States.Default
                        }
                      >
                        {i18n.t(i18nKeys.common__continue)}
                      </Button>
                      {!required && (
                        <Button
                          accessibilityDescribedBy={'skipAbn'}
                          type={Button.Types.Tertiary}
                          onClick={() => setAddAbnLater(true)}
                          state={
                            isAbnSkipLoading
                              ? Button.States.Loading
                              : Button.States.Default
                          }
                        >
                          {i18n.t(i18nKeys.common__skipForNow)}
                        </Button>
                      )}
                      <InlineChildren
                        justifyContent={InlineChildren.Justification.Center}
                      >
                        <Text
                          styles={Text.Styles.Caption1}
                          color={Text.Colors.TextTertiary}
                          textAlign={Text.TextAlignments.Center}
                        >
                          {i18n.t(i18nKeys.backgroundCheck__abnDisclaimer)}
                        </Text>
                      </InlineChildren>
                    </StackChildren>
                  </Inset>
                </StackChildren>
              </form>
            )
          }}
        </Form>
      </StackChildren>
    </>
  )
}

const mapDispatchToProps = {
  trackBackgroundCheckSteps: events.trackBackgroundCheckSteps,
}

export default connect(null, mapDispatchToProps)(AbnPage)
